import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Wand2, Copy, RefreshCw, Check, ChevronDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

// Character sets for password generation
const CHARSETS = {
  lowercase: 'abcdefghijklmnopqrstuvwxyz',
  uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  numbers: '0123456789',
  symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
} as const;

type CharsetKey = keyof typeof CHARSETS;

// Preset configurations for common secret types
const PRESETS = [
  { name: 'Password', length: 16, charsets: ['lowercase', 'uppercase', 'numbers', 'symbols'] as CharsetKey[] },
  { name: 'API Key', length: 32, charsets: ['lowercase', 'uppercase', 'numbers'] as CharsetKey[] },
  { name: 'Token', length: 64, charsets: ['lowercase', 'uppercase', 'numbers'] as CharsetKey[] },
  { name: 'PIN', length: 6, charsets: ['numbers'] as CharsetKey[] },
] as const;

/**
 * Generate a cryptographically secure random string
 */
function generateSecureRandom(length: number, charsets: CharsetKey[]): string {
  const chars = charsets.map(k => CHARSETS[k]).join('');
  if (chars.length === 0) return '';
  
  const array = new Uint32Array(length);
  crypto.getRandomValues(array);
  
  return Array.from(array, (num) => chars[num % chars.length]).join('');
}

interface AddSecretModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (data: { key: string; value: string; description?: string }) => Promise<void>;
  existingKeys: string[];
}

export function AddSecretModal({
  isOpen,
  onClose,
  onAdd,
  existingKeys,
}: AddSecretModalProps) {
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keyInputRef = useRef<HTMLInputElement>(null);
  
  const [showGenerator, setShowGenerator] = useState(false);
  const [genLength, setGenLength] = useState(16);
  const [genCharsets, setGenCharsets] = useState<CharsetKey[]>(['lowercase', 'uppercase', 'numbers', 'symbols']);
  const [justCopied, setJustCopied] = useState(false);

  const handleGenerate = useCallback(() => {
    const generated = generateSecureRandom(genLength, genCharsets);
    setValue(generated);
    setError(null);
  }, [genLength, genCharsets]);

  const handlePreset = useCallback((preset: typeof PRESETS[number]) => {
    setGenLength(preset.length);
    setGenCharsets([...preset.charsets]);
    const generated = generateSecureRandom(preset.length, [...preset.charsets]);
    setValue(generated);
    setError(null);
    setShowGenerator(true);
  }, []);

  const handleCopyGenerated = useCallback(async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setJustCopied(true);
      setTimeout(() => setJustCopied(false), 1500);
    } catch {
      // Clipboard access denied - silent fail
    }
  }, [value]);

  const toggleCharset = useCallback((charset: CharsetKey) => {
    setGenCharsets(prev => {
      if (prev.includes(charset)) {
        if (prev.length === 1) return prev; // Keep at least one
        return prev.filter(c => c !== charset);
      }
      return [...prev, charset];
    });
  }, []);

  useEffect(() => {
    if (isOpen) {
      setKey('');
      setValue('');
      setDescription('');
      setError(null);
      setShowGenerator(false);
      setJustCopied(false);
      setTimeout(() => keyInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const validateKey = (k: string): string | null => {
    if (!k.trim()) {
      return 'Key is required';
    }
    // Standard env var naming: uppercase letters, numbers, underscores
    if (!/^[A-Z][A-Z0-9_]*$/.test(k.trim())) {
      return 'Key must start with a letter and contain only uppercase letters, numbers, and underscores';
    }
    if (existingKeys.includes(k.trim())) {
      return 'A secret with this key already exists';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const keyError = validateKey(key);
    if (keyError) {
      setError(keyError);
      return;
    }

    if (!value) {
      setError('Value is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onAdd({
        key: key.trim(),
        value,
        description: description.trim() || undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add secret');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Secret</DialogTitle>
          <DialogDescription>
            Add a new environment variable to this environment.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="secret-key">
                Key <span className="text-destructive">*</span>
              </Label>
              <Input
                ref={keyInputRef}
                id="secret-key"
                type="text"
                value={key}
                onChange={(e) => {
                  setKey(e.target.value.toUpperCase());
                  setError(null);
                }}
                placeholder="MY_SECRET_KEY"
                className="font-mono"
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground">
                Uppercase letters, numbers, and underscores only
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="secret-value">
                  Value <span className="text-destructive">*</span>
                </Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 gap-1 text-xs text-primary hover:text-primary/80"
                      disabled={isSubmitting}
                    >
                      <Wand2 className="h-3 w-3" />
                      Generate
                      <ChevronDown className="h-3 w-3 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                      Quick presets
                    </DropdownMenuLabel>
                    {PRESETS.map((preset) => (
                      <DropdownMenuItem
                        key={preset.name}
                        onClick={() => handlePreset(preset)}
                        className="cursor-pointer"
                      >
                        <span className="flex-1">{preset.name}</span>
                        <span className="text-xs text-muted-foreground">{preset.length} chars</span>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setShowGenerator(true)}
                      className="cursor-pointer"
                    >
                      Custom options...
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              <div className="flex gap-2">
                <Input
                  id="secret-value"
                  type="text"
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value);
                    setError(null);
                  }}
                  placeholder="Enter secret value"
                  className="font-mono flex-1"
                  disabled={isSubmitting}
                />
                {value && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleCopyGenerated}
                    disabled={isSubmitting}
                    className="shrink-0"
                    title="Copy value"
                  >
                    {justCopied ? (
                      <Check className="h-4 w-4 text-primary" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>

              {showGenerator && (
                <div className="mt-3 p-3 rounded-lg border border-border bg-card/50 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Generator Options</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowGenerator(false)}
                      className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                    >
                      Hide
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="gen-length" className="text-xs text-muted-foreground">
                        Length
                      </Label>
                      <span className="text-xs font-mono text-muted-foreground">
                        {genLength} characters
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        id="gen-length"
                        type="range"
                        min={8}
                        max={128}
                        value={genLength}
                        onChange={(e) => setGenLength(Number(e.target.value))}
                        className="flex-1 h-1.5 bg-secondary rounded-full appearance-none cursor-pointer accent-primary"
                      />
                      <Input
                        type="number"
                        min={8}
                        max={128}
                        value={genLength}
                        onChange={(e) => {
                          const val = Math.min(128, Math.max(8, Number(e.target.value) || 8));
                          setGenLength(val);
                        }}
                        className="w-16 h-7 text-xs font-mono text-center"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Character types</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {(Object.keys(CHARSETS) as CharsetKey[]).map((charset) => (
                        <label
                          key={charset}
                          className="flex items-center gap-2 p-2 rounded border border-border bg-input/50 cursor-pointer hover:bg-input transition-colors"
                        >
                          <Checkbox
                            checked={genCharsets.includes(charset)}
                            onCheckedChange={() => toggleCharset(charset)}
                          />
                          <span className="text-xs capitalize">{charset}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleGenerate}
                    className="w-full gap-2"
                    disabled={genCharsets.length === 0}
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    {value ? 'Regenerate' : 'Generate'}
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="secret-description">
                Description <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="secret-description"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this secret"
                disabled={isSubmitting}
              />
            </div>

            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !key || !value}
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Add Secret
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
